import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dia } from '@joint/plus';

import * as NativeActionService from '../../../../../../../../services/2talk/native-action.service.js';

/**
 * useCellOutputSchema — inferência downstream do shape de uma variável do fluxo.
 *
 * Introduzido em 2026-10-13. Dado o nome da variável (consumida numa caixa downstream),
 * o hook:
 *   1. Traversa o grafo JointJS procurando a cell UPSTREAM (NativeAction) que grava essa var
 *      no `attrs.resultVariable.text`.
 *   2. Pega o `slug` dessa cell, consulta o catálogo de ações nativas, extrai o
 *      `output_schema_json` enriquecido.
 *   3. Normaliza em `{ slug, display_name, fields[], isArray, arrayKey, itemFields[] }`
 *      pros consumers mostrarem chips/sugestões específicas.
 *
 * Semântica das propriedades:
 *   - `fields`:      propriedades do root do output schema (ex.: para `called.list_by_id` →
 *                    [id, title, description, status_id, ...]).
 *   - `isArray`:     true se o output tem UM array como principal (ex.: `calleds`, `attachments`).
 *   - `arrayKey`:    o nome da property que é array (ex.: "attachments").
 *   - `itemFields`:  propriedades de CADA ITEM do array (ex.: para `called.list_attachments` →
 *                    [attachment_id, name, url, path, type, size]).
 *
 * Fallback silencioso: se não encontrar NativeAction upstream OU se o schema não tem
 * properties, retorna `{ fields: [], itemFields: [] }` — consumer cai nos chips estáticos
 * genéricos atuais, sem quebrar.
 *
 * Catálogo é cacheado em escopo de módulo (todas as instâncias do hook compartilham o fetch).
 */

export interface SchemaFieldInfo {
    name: string;
    type?: string;
    title?: string;
    description?: string;
}

export interface CellOutputSchema {
    /** Slug da NativeAction upstream (se detectada). */
    slug: string | null;
    /** Nome amigável da ação (display_name do catálogo). */
    displayName: string | null;
    /** Propriedades do root do output schema. */
    fields: SchemaFieldInfo[];
    /** true se o output tem array como propriedade principal. */
    isArray: boolean;
    /** Nome da property-array (ex.: "attachments", "calleds"). */
    arrayKey: string | null;
    /** Propriedades de cada item do array. */
    itemFields: SchemaFieldInfo[];
    /** true enquanto o catálogo está sendo carregado. */
    loading: boolean;
}

const EMPTY_RESULT: CellOutputSchema = {
    slug: null,
    displayName: null,
    fields: [],
    isArray: false,
    arrayKey: null,
    itemFields: [],
    loading: false,
};

// ---------- Cache em módulo do catálogo ----------
type CatalogEntry = {
    id: number;
    slug: string;
    display_name: string;
    output_schema_json?: string;
};

let catalogCache: CatalogEntry[] | null = null;
let catalogPromise: Promise<CatalogEntry[]> | null = null;

async function loadCatalog(): Promise<CatalogEntry[]> {
    if (catalogCache) return catalogCache;
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async () => {
        try {
            const resp: any = await NativeActionService.getAll();
            const raw: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: CatalogEntry[] = raw.map((c) => ({
                id: Number(c.id ?? c.Id ?? 0),
                slug: String(c.slug ?? c.Slug ?? ''),
                display_name: String(c.display_name ?? c.displayName ?? c.DisplayName ?? ''),
                output_schema_json: c.output_schema_json ?? c.outputSchemaJson ?? c.OutputSchemaJson,
            })).filter((c) => c.id > 0 && c.slug);
            catalogCache = list;
            return list;
        } catch {
            catalogCache = [];
            return catalogCache;
        } finally {
            catalogPromise = null;
        }
    })();
    return catalogPromise;
}

// ---------- Extração de fields do schema ----------
function extractFields(schema: any): SchemaFieldInfo[] {
    if (!schema || typeof schema !== 'object' || !schema.properties) return [];
    const props = schema.properties as Record<string, any>;
    return Object.keys(props).map((name) => ({
        name,
        type: typeof props[name]?.type === 'string' ? props[name].type : undefined,
        title: typeof props[name]?.title === 'string' ? props[name].title : undefined,
        description: typeof props[name]?.description === 'string' ? props[name].description : undefined,
    }));
}

function findArrayProperty(schema: any): { key: string; items: any } | null {
    if (!schema?.properties) return null;
    for (const k of Object.keys(schema.properties)) {
        const p = schema.properties[k];
        if (p?.type === 'array' && p?.items) return { key: k, items: p.items };
    }
    return null;
}

// ---------- Hook ----------
interface Options {
    /** Nome da variável consumida (ex.: "chamado_consultado"). */
    variableName: string;
    /** A cell CONSUMIDORA — usada pra achar o grafo. Hook aceita qualquer dia.Cell. */
    cell: dia.Cell | null | undefined;
}

export function useCellOutputSchema({ variableName, cell }: Options): CellOutputSchema {
    const [resolved, setResolved] = useState<CellOutputSchema>(EMPTY_RESULT);
    const latestNameRef = useRef<string>('');

    const compute = useCallback(async () => {
        const name = (variableName ?? '').trim();
        latestNameRef.current = name;
        if (!name || !cell) {
            setResolved(EMPTY_RESULT);
            return;
        }
        // graph fica em cell.graph (JointJS) OU cell.collection.graph (em algumas versões).
        const graph: dia.Graph | undefined =
            (cell as any).graph ?? (cell as any).collection?.graph;
        if (!graph || typeof graph.getCells !== 'function') {
            setResolved({ ...EMPTY_RESULT, loading: true });
            return;
        }

        // Procura NativeAction upstream com resultVariable == name.
        let foundSlug: string | null = null;
        const cells = graph.getCells();
        for (const c of cells) {
            try {
                const t = c.get('type') as string | undefined;
                if (t !== 'app.NativeAction') continue;
                const rv = (c as any).prop(['attrs', 'resultVariable', 'text']);
                if (typeof rv === 'string' && rv.trim() === name) {
                    const slug = (c as any).prop(['attrs', 'slug', 'text']);
                    if (typeof slug === 'string' && slug) {
                        foundSlug = slug;
                        break;
                    }
                }
            } catch { /* próxima */ }
        }

        if (!foundSlug) {
            if (latestNameRef.current === name) setResolved(EMPTY_RESULT);
            return;
        }

        setResolved({ ...EMPTY_RESULT, loading: true });
        const catalog = await loadCatalog();
        if (latestNameRef.current !== name) return; // race — nome mudou enquanto buscava
        const entry = catalog.find((e) => e.slug === foundSlug);
        if (!entry?.output_schema_json) {
            setResolved({ ...EMPTY_RESULT, slug: foundSlug, displayName: entry?.display_name ?? null });
            return;
        }
        try {
            const schema = JSON.parse(entry.output_schema_json);
            const fields = extractFields(schema);
            const arr = findArrayProperty(schema);
            const itemFields = arr?.items ? extractFields(arr.items) : [];
            setResolved({
                slug: foundSlug,
                displayName: entry.display_name,
                fields,
                isArray: !!arr,
                arrayKey: arr?.key ?? null,
                itemFields,
                loading: false,
            });
        } catch {
            setResolved({ ...EMPTY_RESULT, slug: foundSlug, displayName: entry.display_name });
        }
    }, [variableName, cell]);

    useEffect(() => {
        compute();
    }, [compute]);

    // Re-roda quando o grafo muda (admin pode editar a caixa upstream sem desmontar o inspector).
    useEffect(() => {
        if (!cell) return;
        const graph: dia.Graph | undefined =
            (cell as any).graph ?? (cell as any).collection?.graph;
        if (!graph) return;
        const handler = () => { compute(); };
        graph.on('change:attrs add remove', handler);
        return () => { graph.off('change:attrs add remove', handler); };
    }, [cell, compute]);

    return useMemo(() => resolved, [resolved]);
}
